import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { formatDateDisplay } from '../../utils/date';
import { money } from './money';
import { isUuid } from './recordLinks';
import {
  ADMIN_SEARCH_LIMIT,
  buildSearchIndex,
  fullName,
  one,
  searchLocalIndex,
  type EnrollmentIndexRow,
  type LocalSearchResult,
  type ParentIndexRow,
  type RecordHit,
  type StudentIndexRow,
} from './searchIndex';

export type { RecordHit, RecordKind } from './searchIndex';

export interface AdminSearchResult extends LocalSearchResult {
  invoices: RecordHit[];
}

/** One letter matches most of the school; the directory pages are the place for that. */
export const ADMIN_SEARCH_MIN_CHARS = 2;
const DEBOUNCE_MS = 200;
/** PostgREST returns at most this many rows per request. */
const PAGE_SIZE = 1000;

const NO_LOCAL_RESULTS: LocalSearchResult = { students: [], parents: [], enrollments: [] };

/** Every row of a table, a page at a time, in a stable order so no row is read twice or skipped. */
async function fetchAll(
  page: (from: number, to: number) => PromiseLike<{ data: unknown[] | null; error: unknown }>,
): Promise<unknown[]> {
  const rows: unknown[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await page(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < PAGE_SIZE) return rows;
  }
}

async function loadSearchIndex() {
  const [students, parents, enrollments] = await Promise.all([
    fetchAll((from, to) => supabase
      .from('students')
      .select('id, first_name, last_name, active, school:metadata->>school, grade:metadata->>grade, labels:metadata->labels, student_parents(parent:parents(first_name, last_name, email))')
      .order('id')
      .range(from, to)),
    fetchAll((from, to) => supabase
      .from('parents')
      .select('id, first_name, last_name, email, phone, active, student_parents(student:students(first_name, last_name))')
      .order('id')
      .range(from, to)),
    fetchAll((from, to) => supabase
      .from('enrollments')
      .select('id, student_id, is_active, enrollment_start_date, created_at, programs(name, type)')
      .order('id')
      .range(from, to)),
  ]);
  return buildSearchIndex(
    students as StudentIndexRow[],
    parents as ParentIndexRow[],
    enrollments as EnrollmentIndexRow[],
  );
}

/**
 * Students, parents and enrollments, held in the browser so search answers as you type.
 *
 * All three are small (hundreds of rows) and change slowly, so a copy that is a couple of
 * minutes old is fine. It is fetched in the background soon after any admin page loads (see
 * AdminLayout), so it is usually ready before ⌘K is pressed, and reloaded behind the scenes
 * when search opens on a copy older than that. Invoices are not in it: they are many, and
 * their status changes with every payment.
 */
export const searchIndexQuery = queryOptions({
  queryKey: ['admin', 'search-index'],
  queryFn: loadSearchIndex,
  staleTime: 2 * 60_000,
  gcTime: 30 * 60_000,
});

interface InvoiceRow {
  id: string;
  total_amount: number | null;
  status: string | null;
  period_start: string | null;
  memo_code: string | null;
  enrollments: { students: unknown; programs: unknown } | Array<{ students: unknown; programs: unknown }> | null;
}

const INVOICE_SELECT = 'id, total_amount, status, period_start, memo_code, enrollments!inner(students!inner(first_name, last_name), programs!inner(name))';

async function invoiceHits(ids: string[]): Promise<RecordHit[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase.from('invoices').select(INVOICE_SELECT).in('id', ids);
  if (error) throw error;
  const index = new Map(ids.map((id, i) => [id, i]));
  return ((data ?? []) as unknown as InvoiceRow[])
    .sort((a, b) => (index.get(a.id) ?? 0) - (index.get(b.id) ?? 0))
    .map(i => {
      const enrollment = one(i.enrollments);
      const student = one(enrollment?.students as { first_name: string | null; last_name: string | null } | null);
      const program = one(enrollment?.programs as { name: string | null } | null);
      return {
        id: i.id,
        kind: 'invoice' as const,
        name: `${fullName(student?.first_name, student?.last_name)} · ${program?.name ?? 'Unknown program'}`,
        // The memo code last: it is what a Zelle alert quotes, so it is what gets searched for.
        detail: [
          i.period_start ? formatDateDisplay(i.period_start) : null,
          money(i.total_amount),
          i.status,
          i.memo_code,
        ].filter(Boolean).join(' · '),
        badge: i.status === 'voided' ? 'Voided' : null,
      };
    });
}

// The same server-side matching the Finance Ops search box uses: the child, program,
// enrolling parent, status, dates or Zelle memo code. Newest first: the invoice someone is
// asking about is rarely last year's.
async function searchInvoices(keywords: string[]): Promise<RecordHit[]> {
  const { data, error } = await supabase.rpc('search_invoices', {
    p_keywords: keywords,
    p_start_date: null,
    p_end_date: null,
    p_status: 'all',
    p_limit: ADMIN_SEARCH_LIMIT,
    p_offset: 0,
    p_sort_column: 'created_at',
    p_sort_direction: 'desc',
  });
  if (error) throw error;
  return invoiceHits(((data ?? []) as Array<{ invoice_id: string }>).map(row => row.invoice_id));
}

export function toKeywords(term: string): string[] {
  return term.toLowerCase().trim().split(/\s+/).filter(Boolean);
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Students, parents, enrollments and invoices matching what the admin has typed so far.
 *
 * People and enrollments come from the in-browser index, so they answer on every keystroke.
 * Invoices are searched on the server, debounced, and the previous answer stays on screen
 * while the next one loads — but only while the term is long enough to search at all:
 * backspacing to one letter clears it rather than leaving the last answer behind.
 */
export function useAdminSearch(term: string): AdminSearchResult & { isSearching: boolean; isError: boolean } {
  const live = term.trim();
  const active = live.length >= ADMIN_SEARCH_MIN_CHARS;

  const index = useQuery(searchIndexQuery);
  const local = useMemo(
    () => (active && index.data ? searchLocalIndex(index.data, live) : NO_LOCAL_RESULTS),
    [active, index.data, live],
  );

  const debounced = useDebouncedValue(live, DEBOUNCE_MS);
  const keywords = toKeywords(debounced);
  const invoices = useQuery({
    queryKey: ['admin', 'invoice-search', keywords.join(' ')],
    queryFn: () => (isUuid(debounced) ? invoiceHits([debounced.toLowerCase()]) : searchInvoices(keywords)),
    enabled: debounced.length >= ADMIN_SEARCH_MIN_CHARS,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  return {
    ...local,
    invoices: active ? invoices.data ?? [] : [],
    // Only the index's first load counts: a background refresh keeps showing the old copy.
    isSearching: active && (index.isPending || live !== debounced || invoices.isFetching),
    isError: active && (index.isError || invoices.isError),
  };
}
